import TomSelect from 'tom-select';

const instances = new Set();

// Shared by server-rendered controls and promotion editors with client-side options.
export function createTomSelect(element, settings = {}) {
    if (typeof element === 'string') element = document.querySelector(element);
    if (!element || element.tomselect) return element?.tomselect;

    const multiple = element.multiple;
    const modal = element.closest('.modal');
    const select = new TomSelect(element, {
        maxItems: multiple ? null : 1,
        maxOptions: null,
        create: false,
        dropdownParent: modal || 'body',
        placeholder: element.getAttribute('placeholder') || element.title || 'Seleccione...',
        plugins: multiple ? ['remove_button', 'dropdown_input'] : ['dropdown_input', 'clear_button'],
        render: {
            no_results: (data, escape) => `<div class="no-results">No hay resultados para ${escape(data.input)}</div>`,
        },
        ...settings,
    });
    instances.add(select);
    select.on('destroy', () => instances.delete(select));

    select.dropdown.classList.add('app-tom-dropdown');
    select.control_input.placeholder = element.dataset.searchPlaceholder || 'Buscar...';

    // Keep modal focus trapping intact, without clipping the menu in its scrollable body.
    if (modal) {
        select.positionDropdown = function () {
            const field = this.control.getBoundingClientRect();
            const parent = modal.getBoundingClientRect();
            Object.assign(this.dropdown.style, {
                width: `${field.width}px`,
                left: `${field.left - parent.left + modal.scrollLeft}px`,
                top: `${field.bottom - parent.top + modal.scrollTop}px`,
            });
        };
    }
    const reposition = () => { if (select.isOpen) select.positionDropdown(); };
    window.addEventListener('scroll', reposition, true);
    select.on('destroy', () => window.removeEventListener('scroll', reposition, true));

    if (multiple && element.dataset.bulkActions === 'true') {
        const actions = document.createElement('div');
        actions.className = 'd-flex gap-2 p-2 border-bottom';
        const addAction = (label, action) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'btn btn-sm btn-outline-secondary';
            button.textContent = label;
            button.addEventListener('mousedown', event => event.preventDefault());
            button.addEventListener('click', event => {
                event.preventDefault();
                event.stopPropagation();
                if (!select.isDisabled && !select.isReadOnly) action();
            });
            actions.append(button);
        };
        addAction(element.dataset.selectAllText || 'Seleccionar todos', () => {
            const values = Object.values(select.options)
                .filter(option => !option.disabled && option.value !== '')
                .map(option => String(option.value));
            select.setValue(values);
        });
        addAction(element.dataset.deselectAllText || 'Deseleccionar todos', () => select.clear());
        select.dropdown.insertBefore(actions, select.dropdown_content);
    }

    const countThreshold = Number(element.dataset.countThreshold);
    if (multiple && Number.isFinite(countThreshold) && element.dataset.countThreshold) {
        const summary = document.createElement('span');
        summary.className = 'tom-select-count text-truncate';
        const updateCount = () => {
            const show = select.items.length > countThreshold;
            select.wrapper.classList.toggle('tom-select-counted', show);
            summary.hidden = !show;
            summary.textContent = (element.dataset.countLabel || '{0} seleccionados').replace('{0}', select.items.length);
            select.control.prepend(summary);
        };
        select.on('change', updateCount);
        select.on('item_add', updateCount);
        select.on('item_remove', updateCount);
        select.on('clear', updateCount);
        updateCount();
    }

    if (element.hasAttribute('readonly')) select.setReadOnly(true);
    return select;
}

const fields = new Map();

function copyOptions(source, control) {
    control.innerHTML = source.innerHTML;
    // Only the source participates in Livewire's keyed DOM updates.
    control.querySelectorAll('[wire\\:key]').forEach(option => option.removeAttribute('wire:key'));
}

function findWireForField(field) {
    const componentRoot = field.closest('[wire\\:id]');
    if (!componentRoot || !window.Livewire) return null;

    const componentId = componentRoot.getAttribute('wire:id');

    // A modal may briefly retain the id of a component removed from
    // Livewire's store while the DOM is being updated.
    try {
        return window.Livewire.find(componentId);
    } catch (error) {
        return null;
    }
}

export function syncTomSelectFields(root = document) {
    for (const select of instances) {
        if (!select.input.isConnected) select.destroy();
    }
    for (const [field, state] of fields) {
        const currentControl = field.querySelector('[data-tom-select-control]');
        const controlWasReplaced = !state.select.input.isConnected || currentControl !== state.select.input;

        if (!field.isConnected || controlWasReplaced) {
            if (instances.has(state.select)) state.select.destroy();
            fields.delete(field);
        }
    }

    root.querySelectorAll('[data-tom-select-field]').forEach(field => {
        const source = field.querySelector('[data-tom-select-source]');
        const control = field.querySelector('[data-tom-select-control]');
        const wire = findWireForField(field);
        const model = field.dataset.model;
        let state = fields.get(field);
        if (!state) {
            copyOptions(source, control);
            const select = createTomSelect(control);
            state = { select, options: source.innerHTML };
            fields.set(field, state);
            select.on('change', value => {
                const currentWire = findWireForField(field);
                if (currentWire && model) {
                    currentWire.set(
                        model,
                        control.multiple ? [...value] : (value || null),
                        field.dataset.defer === 'true'
                    );
                }
            });
        }

        const select = state.select;
        const value = wire && model
            ? wire.__instance.getPropertyValueIncludingDefers(model)
            : select.getValue();

        if (state.options !== source.innerHTML) {
            select.clear(true);
            select.clearOptions();
            copyOptions(source, control);
            select.sync();
            state.options = source.innerHTML;
        }

        const next = control.multiple ? (value || []).map(String) : String(value ?? '');
        if (JSON.stringify(select.getValue()) !== JSON.stringify(next)) select.setValue(next, true);
        source.disabled ? select.disable() : select.enable();
        select.setReadOnly(source.hasAttribute('readonly'));
        select.wrapper.classList.toggle('is-invalid', source.classList.contains('is-invalid'));
    });
}

export function installTomSelectFields() {
    let bound = false;
    let cleanupScheduled = false;
    const initialize = () => {
        syncTomSelectFields();
        if (!window.Livewire || bound) return;
        bound = true;
        window.Livewire.hook('message.processed', (message, component) => {
            syncTomSelectFields(component.el);

            // Livewire 2 can finish replacing a child inside wire:ignore immediately
            // after this hook. Recheck on the next frame so Tom Select owns the final node.
            window.requestAnimationFrame(() => syncTomSelectFields(component.el));
        });
        window.Livewire.hook('element.removed', () => {
            if (cleanupScheduled) return;
            cleanupScheduled = true;
            queueMicrotask(() => {
                cleanupScheduled = false;
                syncTomSelectFields();
            });
        });
    };
    document.addEventListener('livewire:load', initialize);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
    else initialize();
}
